import { useEffect, useState } from "react";
import { ArrowLeft, LockKeyhole, Phone, ShieldCheck, X } from "lucide-react";
import {
  clearCustomerRecaptcha,
  friendlyCustomerAuthError,
  sendCustomerOtp,
  verifyCustomerOtp,
} from "./customerAuth";

const RECAPTCHA_CONTAINER_ID = "quickdrop-customer-recaptcha";

export function CustomerLogin({ open, onClose, onSuccess }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) return undefined;
    clearCustomerRecaptcha();
    setStep("phone");
    setPhone("");
    setOtp("");
    setConfirmation(null);
    setLoading(false);
    setError("");
    return undefined;
  }, [open]);

  useEffect(() => () => clearCustomerRecaptcha(), []);

  if (!open) return null;

  const close = () => {
    if (!loading) onClose();
  };

  const requestOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await sendCustomerOtp(phone, RECAPTCHA_CONTAINER_ID);
      setConfirmation(result);
      setStep("otp");
    } catch (requestError) {
      setError(friendlyCustomerAuthError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const credential = await verifyCustomerOtp(confirmation, otp);
      clearCustomerRecaptcha();
      onSuccess(credential.user);
      onClose();
    } catch (verificationError) {
      setError(friendlyCustomerAuthError(verificationError));
    } finally {
      setLoading(false);
    }
  };

  const editPhone = () => {
    clearCustomerRecaptcha();
    setStep("phone");
    setOtp("");
    setConfirmation(null);
    setError("");
  };

  return <div className="qd-login-backdrop" role="presentation">
    <section className="qd-login-dialog" role="dialog" aria-modal="true" aria-labelledby="qd-login-title">
      <button className="qd-login-close" type="button" onClick={close} disabled={loading} aria-label="Close customer login"><X /></button>
      <div className="qd-login-icon">{step === "phone" ? <Phone /> : <LockKeyhole />}</div>
      <span>QUICKDROP CUSTOMER</span>
      <h2 id="qd-login-title">{step === "phone" ? "Login with mobile" : "Enter your OTP"}</h2>
      <p>{step === "phone" ? "We’ll send a one-time password to verify your Indian mobile number." : `Enter the 6-digit OTP sent to +91 ${phone}.`}</p>

      {step === "phone" ? <form onSubmit={requestOtp}>
        <label htmlFor="qd-customer-phone">Mobile number</label>
        <div className="qd-login-input"><span>+91</span><input id="qd-customer-phone" aria-label="Indian mobile number" inputMode="numeric" autoComplete="tel-national" placeholder="9876543210" maxLength={10} value={phone} onChange={(event) => setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))} disabled={loading} autoFocus /></div>
        {error && <div className="qd-login-error" role="alert">{error}</div>}
        <button className="qd-login-submit" type="submit" disabled={loading}>{loading ? "Sending OTP…" : "Send OTP"}</button>
      </form> : <form onSubmit={verifyOtp}>
        <label htmlFor="qd-customer-otp">6-digit OTP</label>
        <input className="qd-otp-input" id="qd-customer-otp" aria-label="6-digit OTP" inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} disabled={loading} autoFocus />
        {error && <div className="qd-login-error" role="alert">{error}</div>}
        <button className="qd-login-submit" type="submit" disabled={loading}>{loading ? "Verifying…" : "Verify & login"}</button>
        <button className="qd-login-back" type="button" onClick={editPhone} disabled={loading}><ArrowLeft /> Change mobile number</button>
      </form>}

      <div id={RECAPTCHA_CONTAINER_ID} />
      <div className="qd-login-safe"><ShieldCheck /><span><b>Secure Firebase verification</b><small>Your OTP is verified by Firebase Authentication.</small></span></div>
    </section>
  </div>;
}
